import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useFetch, useUsers, useClientsList } from "@/lib/hooks";
import { TIPI, tipoOf, colorIndex } from "@/lib/calendar";
import { CANALI, statoPostOf } from "@/lib/ped";
import { PageHeader } from "@/components/core/AppLayout";
import { AppointmentsWorkspace } from "@/components/calendar/AppointmentsWorkspace";
import { MobileFilters } from "@/components/core/MobileFilters";
import { Link } from "react-router-dom";

const Pick = ({ value, onChange, options, all, testid, width = "w-[200px]" }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={`${width} rounded-xl bg-[#A6DAFC]`} data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>
      {all && <SelectItem value="all">{all}</SelectItem>}
      {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

const Toggle = ({ checked, onChange, label, testid, disabled, badge }) => (
  <label className={`inline-flex items-center gap-2 text-sm font-semibold ${disabled ? "text-slate-400" : "text-slate-700"}`}>
    <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={testid} />{label}
    {badge && <span className="text-[9px] uppercase tracking-wider font-bold bg-[#E8173A] text-white rounded-full px-2 py-0.5">{badge}</span>}
  </label>
);

export default function CalendarPage() {
  const [cliente, setCliente] = useState("all");
  const [socio, setSocio] = useState("all");
  const [mode, setMode] = useState("cliente");
  const [showTasks, setShowTasks] = useState(true);
  const [showPosts, setShowPosts] = useState(true);
  const [canale, setCanale] = useState("all");
  const { data: users } = useUsers();
  const { data: clients } = useClientsList();
  const [range, setRange] = useState(null);
  const onRange = (r) => setRange((old) => (old && old.da === r.da && old.a === r.a ? old : r));
  const p = new URLSearchParams(range || {});
  if (cliente !== "all") p.set("client_id", cliente);
  if (socio !== "all") p.set("socio_id", socio);
  const { data: appts, reload } = useFetch(range ? `/appointments?${p.toString()}` : null);
  const tp = new URLSearchParams(range ? { scad_da: range.da.slice(0, 10), scad_a: range.a.slice(0, 10) } : {});
  if (cliente !== "all") tp.set("client_id", cliente);
  if (socio !== "all") tp.set("assignee_id", socio);
  const { data: tasks } = useFetch(range && showTasks ? `/tasks?${tp.toString()}` : null);
  const pp = new URLSearchParams(range || {});
  if (cliente !== "all") pp.set("client_id", cliente);
  if (socio !== "all") pp.set("socio_id", socio);
  if (canale !== "all") pp.set("canale", canale);
  const { data: posts } = useFetch(range && showPosts ? `/posts?${pp.toString()}` : null);
  const postEvents = showPosts ? (posts || []).map((x) => {
    const s = statoPostOf(x.stato);
    return { id: `post-${x.id}`, title: `${x.modifiche_richieste ? "● Modifiche richieste · " : ""}PED · ${x.titolo} · ${x.client_nome}`, start: x.data_pubblicazione,
      backgroundColor: x.modifiche_richieste ? "#FBD3DA" : "#A6DAFC", borderColor: x.modifiche_richieste ? "#E8173A" : s.color,
      textColor: x.modifiche_richieste ? "#9F1239" : "#0F172A", classNames: ["core-ev-post", ...(x.modifiche_richieste ? ["core-ev-changes"] : [])], extendedProps: { kind: "post", post: x } };
  }) : [];

  const clientIds = (clients || []).map((c) => c.id);
  const userIds = (users || []).map((u) => u.id);
  const colorOf = (a) => (mode === "cliente" ? colorIndex(clientIds, a.client_id) : mode === "socio" ? colorIndex(userIds, a.partecipanti[0]) : tipoOf(a.tipo).color);
  const legend = mode === "cliente" ? (clients || []).map((c) => ({ id: c.id, label: c.ragione_sociale, color: colorIndex(clientIds, c.id) }))
    : mode === "socio" ? (users || []).map((u) => ({ id: u.id, label: `${u.nome} ${u.cognome}`, color: colorIndex(userIds, u.id) }))
    : TIPI.map((t) => ({ id: t.value, label: t.label, color: t.color }));

  const taskEvents = showTasks ? (tasks || []).filter((t) => t.scadenza && t.stato !== "completata").map((t) => ({
    id: `task-${t.id}`, title: `Scadenza: ${t.titolo}`, start: t.scadenza, allDay: true,
    backgroundColor: "#FBD3DA", borderColor: "#EE8394", textColor: "#9F1239", classNames: ["core-ev-task"], extendedProps: { kind: "task", task: t },
  })) : [];

  const toolbar = (
    <MobileFilters testid="calendar-filters">
      <Pick value={cliente} onChange={setCliente} all="Tutti i clienti" testid="calendar-filter-cliente" options={(clients || []).map((c) => ({ value: c.id, label: c.ragione_sociale }))} />
      <Pick value={socio} onChange={setSocio} all="Tutti i soci" testid="calendar-filter-socio" options={(users || []).map((u) => ({ value: u.id, label: `${u.nome} ${u.cognome}` }))} />
      <Pick value={mode} onChange={setMode} testid="calendar-color-mode" width="w-[190px]"
        options={[{ value: "cliente", label: "Colore per cliente" }, { value: "socio", label: "Colore per socio" }, { value: "tipo", label: "Colore per tipo" }]} />
      <Toggle checked={showTasks} onChange={setShowTasks} label="Scadenze task" testid="calendar-toggle-tasks" />
      <Toggle checked={showPosts} onChange={setShowPosts} label="Post PED" testid="calendar-toggle-ped" />
      {showPosts && <Pick value={canale} onChange={setCanale} all="Tutti i canali" testid="calendar-filter-canale" width="w-[170px]" options={CANALI.map((c) => ({ value: c.value, label: c.label }))} />}
    </MobileFilters>
  );

  return (
    <div data-testid="calendar-page">
      <PageHeader eyebrow="Agenda" title="Calendario" subtitle="Tutti gli appuntamenti con i clienti e le scadenze delle task." />
      {clients && !clients.length && (
        <div className="mb-5 flex items-center justify-between gap-4 rounded-2xl bg-white/70 border border-[#5FB3EA] px-5 py-4" data-testid="calendar-empty-cta">
          <div className="text-sm text-[#0A1128]"><strong>Il calendario è vuoto.</strong> Crea o importa i tuoi clienti: poi potrai fissare appuntamenti, invitarli via email e vedere qui post e scadenze.</div>
          <Link to="/clienti" className="shrink-0 rounded-full bg-[#E8173A] hover:bg-[#C8102E] text-white font-bold text-sm px-5 py-2" data-testid="calendar-empty-clients">Vai ai clienti</Link>
        </div>
      )}
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 mb-4" data-testid="calendar-legend">
        {legend.map((l) => <span key={l.id} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600"><span className="h-2.5 w-2.5 rounded-full" style={{ background: l.color }} />{l.label}</span>)}
        {showTasks && <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600"><span className="h-2.5 w-2.5 rounded-sm border border-red-300 bg-red-50" />Scadenze task</span>}
        {showPosts && <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600"><span className="h-2.5 w-2.5 rounded-sm border-2 border-l-4 border-blue-500 bg-[#A6DAFC]" />Post PED (bordo = stato)</span>}
      </div>
      <AppointmentsWorkspace appointments={appts || []} reload={reload} colorOf={colorOf} titleOf={(a) => `${a.titolo} · ${a.client_nome}`}
        extraEvents={[...taskEvents, ...postEvents]} clients={(clients || []).filter((c) => c.stato === "attivo")} users={users || []} toolbar={toolbar} onRangeChange={onRange} />
    </div>
  );
}
